import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydSelect from '../../../commonDesign/SydSelect'
import SydInput from '../../../commonDesign/SydInput'
import SydLabel from '../../../commonDesign/SydLabel'
import SydCheckbox from '../../../commonDesign/SydCheckbox'
import { usePreviewManagedSchedule } from '../../../../api/scheduler'
import useDebouncedValue from '../../../../hooks/useDebounceValue'

const WILDCARD = '*'
const QUESTION = '?'

const MINUTES = [
  { label: 'Every minute (*)', value: WILDCARD },
  ...Array.from({ length: 60 }, (_, i) => ({ label: String(i), value: String(i) }))
]

const HOURS = [
  { label: 'Every hour (*)', value: WILDCARD },
  ...Array.from({ length: 24 }, (_, i) => ({ label: String(i), value: String(i) }))
]

const DAYS_OF_MONTH = [
  { label: 'Every day (*)', value: WILDCARD },
  { label: 'No specific day (?)', value: QUESTION },
  ...Array.from({ length: 31 }, (_, i) => ({ label: String(i + 1), value: String(i + 1) }))
]

const MONTHS = [
  { label: 'Every month (*)', value: WILDCARD },
  { label: '1 - January', value: '1' },
  { label: '2 - February', value: '2' },
  { label: '3 - March', value: '3' },
  { label: '4 - April', value: '4' },
  { label: '5 - May', value: '5' },
  { label: '6 - June', value: '6' },
  { label: '7 - July', value: '7' },
  { label: '8 - August', value: '8' },
  { label: '9 - September', value: '9' },
  { label: '10 - October', value: '10' },
  { label: '11 - November', value: '11' },
  { label: '12 - December', value: '12' }
]

const DAYS_OF_WEEK = [
  { label: 'Every day (*)', value: WILDCARD },
  { label: 'No specific day (?)', value: QUESTION },
  { label: 'SUN', value: 'SUN' },
  { label: 'MON', value: 'MON' },
  { label: 'TUE', value: 'TUE' },
  { label: 'WED', value: 'WED' },
  { label: 'THU', value: 'THU' },
  { label: 'FRI', value: 'FRI' },
  { label: 'SAT', value: 'SAT' }
]

const YEARS = (() => {
  const start = new Date().getFullYear()
  const yearOpts = [{ label: 'Every year (*)', value: WILDCARD }]
  for (let y = start; y < start + 5; y++) {
    yearOpts.push({ label: String(y), value: String(y) })
  }
  return yearOpts
})()

const PRESETS = [
  { label: 'Select a preset...', value: '' },
  { label: 'Daily at 8 AM', value: '0 8 * * ? *' },
  { label: 'Hourly', value: '0 * * * ? *' },
  { label: 'Every 15 minutes', value: '0/15 * * * ? *' },
  { label: 'Weekly Mon at 8 AM', value: '0 8 ? * MON *' },
  { label: 'Monthly 1st at 8 AM', value: '0 8 1 * ? *' },
  { label: 'Custom', value: 'custom' }
]

function parseCronFields (expression) {
  const match = expression?.match(/^cron\((.+)\)$/)
  if (!match) return null
  const parts = match[1].split(/\s+/)
  if (parts.length !== 6) return null
  return {
    minute: parts[0],
    hour: parts[1],
    dayOfMonth: parts[2],
    month: parts[3],
    dayOfWeek: parts[4],
    year: parts[5]
  }
}

function buildCronExpression (fields) {
  return `cron(${fields.minute} ${fields.hour} ${fields.dayOfMonth} ${fields.month} ${fields.dayOfWeek} ${fields.year})`
}

const DEFAULT_FIELDS = {
  minute: '0',
  hour: '8',
  dayOfMonth: WILDCARD,
  month: WILDCARD,
  dayOfWeek: QUESTION,
  year: WILDCARD
}

function isSimpleValue (val, opts) {
  return opts.some(o => o.value === val)
}

function CronBuilder ({ value, timezone, onChange }) {
  const [rawMode, setRawMode] = useState(false)
  const [fields, setFields] = useState(DEFAULT_FIELDS)
  const [rawValue, setRawValue] = useState(value || '')
  const lastEmittedRef = useRef(value)

  useEffect(() => {
    if (!value || value === lastEmittedRef.current) return
    const parsed = parseCronFields(value)
    if (parsed) {
      const canRepresent =
        isSimpleValue(parsed.minute, MINUTES) &&
        isSimpleValue(parsed.hour, HOURS) &&
        isSimpleValue(parsed.dayOfMonth, DAYS_OF_MONTH) &&
        isSimpleValue(parsed.month, MONTHS) &&
        isSimpleValue(parsed.dayOfWeek, DAYS_OF_WEEK) &&
        isSimpleValue(parsed.year, YEARS)
      if (canRepresent) {
        setFields(parsed)
        setRawMode(false)
      } else {
        setRawMode(true)
      }
    } else {
      setRawMode(true)
    }
    setRawValue(value)
    lastEmittedRef.current = value
  }, [value])

  const detectedPreset = useMemo(() => {
    const expr = `${fields.minute} ${fields.hour} ${fields.dayOfMonth} ${fields.month} ${fields.dayOfWeek} ${fields.year}`
    const match = PRESETS.find(p => p.value === expr)
    return match ? match.value : 'custom'
  }, [fields])

  const handleFieldChange = useCallback((field) => (e) => {
    const val = e?.target ? e.target.value : e
    setFields(prev => {
      const next = { ...prev, [field]: val }
      if (field === 'dayOfMonth' && val !== WILDCARD && val !== QUESTION) {
        next.dayOfWeek = QUESTION
      } else if (field === 'dayOfWeek' && val !== WILDCARD && val !== QUESTION) {
        next.dayOfMonth = QUESTION
      }
      const expr = buildCronExpression(next)
      lastEmittedRef.current = expr
      onChange(expr)
      return next
    })
  }, [onChange])

  const handlePresetChange = useCallback((e) => {
    const preset = e?.target ? e.target.value : e
    if (!preset || preset === 'custom') return
    const parts = preset.split(/\s+/)
    if (parts.length !== 6) return
    const next = {
      minute: parts[0],
      hour: parts[1],
      dayOfMonth: parts[2],
      month: parts[3],
      dayOfWeek: parts[4],
      year: parts[5]
    }
    setFields(next)
    const expr = buildCronExpression(next)
    lastEmittedRef.current = expr
    onChange(expr)
  }, [onChange])

  const handleRawChange = useCallback((e) => {
    const val = e?.target ? e.target.value : e
    setRawValue(val)
    lastEmittedRef.current = val
    onChange(val)
  }, [onChange])

  const handleModeToggle = useCallback((_event, checked) => {
    setRawMode(checked)
    if (!checked) {
      const parsed = parseCronFields(rawValue)
      if (parsed) {
        setFields(parsed)
        const expr = buildCronExpression(parsed)
        lastEmittedRef.current = expr
        onChange(expr)
      } else {
        setFields(DEFAULT_FIELDS)
        const expr = buildCronExpression(DEFAULT_FIELDS)
        setRawValue(expr)
        lastEmittedRef.current = expr
        onChange(expr)
      }
    } else {
      const expr = buildCronExpression(fields)
      setRawValue(expr)
    }
  }, [rawValue, fields, onChange])

  const currentExpr = rawMode ? rawValue : buildCronExpression(fields)
  const debouncedExpr = useDebouncedValue(currentExpr, 500)

  const { data: preview } = usePreviewManagedSchedule(debouncedExpr, timezone)

  if (rawMode) {
    return (
      <div>
        <SydLabel label='Expression' required helperText='e.g. cron(0 8 * * ? *) or rate(1 hour)'>
          <SydInput value={rawValue} onChange={handleRawChange} placeholder='cron(0 8 * * ? *)' />
        </SydLabel>
        <div style={{ marginTop: 4 }}>
          <SydCheckbox
            value={rawMode}
            onChange={handleModeToggle}
            label='Use raw expression'
          />
        </div>
        {preview?.fireTimes?.length > 0 && (
          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 4 }}>
            <strong>Next fires:</strong>{' '}
            {preview.fireTimes.slice(0, 5).map((t, i) => (
              <span key={i}>{i > 0 && ', '}{new Date(t).toLocaleString()}</span>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <SydLabel label='Preset'>
            <SydSelect
              options={PRESETS}
              value={detectedPreset}
              onChange={handlePresetChange}
              minWidth={0}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={4}>
          <SydLabel label='Minute'>
            <SydSelect options={MINUTES} value={fields.minute} onChange={handleFieldChange('minute')} minWidth={0} />
          </SydLabel>
        </Grid>
        <Grid item xs={4}>
          <SydLabel label='Hour'>
            <SydSelect options={HOURS} value={fields.hour} onChange={handleFieldChange('hour')} minWidth={0} />
          </SydLabel>
        </Grid>
        <Grid item xs={4}>
          <SydLabel label='Day of Month'>
            <SydSelect options={DAYS_OF_MONTH} value={fields.dayOfMonth} onChange={handleFieldChange('dayOfMonth')} minWidth={0} />
          </SydLabel>
        </Grid>
        <Grid item xs={4}>
          <SydLabel label='Month'>
            <SydSelect options={MONTHS} value={fields.month} onChange={handleFieldChange('month')} minWidth={0} />
          </SydLabel>
        </Grid>
        <Grid item xs={4}>
          <SydLabel label='Day of Week'>
            <SydSelect options={DAYS_OF_WEEK} value={fields.dayOfWeek} onChange={handleFieldChange('dayOfWeek')} minWidth={0} />
          </SydLabel>
        </Grid>
        <Grid item xs={4}>
          <SydLabel label='Year'>
            <SydSelect options={YEARS} value={fields.year} onChange={handleFieldChange('year')} minWidth={0} />
          </SydLabel>
        </Grid>
      </Grid>
      <div style={{ marginTop: 4 }}>
        <SydCheckbox
          value={rawMode}
          onChange={handleModeToggle}
          label='Use raw expression'
        />
      </div>
      {preview?.fireTimes?.length > 0 && (
        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 4 }}>
          <strong>Next fires:</strong>{' '}
          {preview.fireTimes.slice(0, 5).map((t, i) => (
            <span key={i}>{i > 0 && ', '}{new Date(t).toLocaleString()}</span>
          ))}
        </div>
      )}
    </div>
  )
}

CronBuilder.propTypes = {
  value: PropTypes.string,
  timezone: PropTypes.string,
  onChange: PropTypes.func.isRequired
}

export default CronBuilder
